import '@eid-easy/eideasy-widget';
import React, { useEffect, useRef } from 'react';
import {
  isProduction,
  isLocalEnvironment,
} from 'js/sign-components/common/ts-utils';
import { useIntl } from 'react-intl';
import * as Sentry from '@sentry/browser';
import { Box } from '@dropbox/dig-foundations';

export function EidWidget({
  qesUrl,
  clientId,
  countryCode,
  hasAdditionalSigningMethods,
}: {
  qesUrl: string;
  clientId: string;
  countryCode?: string;
  hasAdditionalSigningMethods?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { locale } = useIntl();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const eidEasyWidget = document.createElement('eideasy-widget');
    const excludedSignatureTypes = ['evrotrust-signature'];
    const excludedCountries = ['AX', 'BE', 'CH', 'DK', 'FI', 'NO', 'SE'];
    let enabledSignatureMethods = [
      'be-id-signature',
      'dk-mitid-advanced-signature',
      'evrotrust-signature',
      'fi-id-signature',
      'itsme-qes-signature',
      'norwegian-bankid-adv-signature',
      'se-bankid-advanced-signature',
      'spid-qes-signature',
      'swiss-com-qes-signature',
      'gse-adv-signature',
      'cl-ecert-advanced-signature',
      'serpro-id-advanced-signature',
    ];

    if (hasAdditionalSigningMethods) {
      enabledSignatureMethods = enabledSignatureMethods.concat([
        // Here for convenience when additional signing methods are enabled
        // See: https://dropbox.atlassian.net/wiki/spaces/SIGNAPI/pages/2150500233/eID+Easy+Adding+Additional+Signing+Methods
      ]);
    }

    const lang = locale.split('-')?.[0] ?? 'en';

    const settings = {
      debug: isLocalEnvironment(),
      // ISO 3166  two letter country code
      // Users select their own country via dropdown on widget
      countryCode: countryCode ?? 'US',
      language: lang, // ISO 639-1 two letter language code,
      sandbox: !isProduction(),
      clientId,
      docId: qesUrl,
      enabledMethods: {
        signature: enabledSignatureMethods,
      },
      enabledCountries: 'all',
      onSuccess: (result: { data: { signature_redirect: string } }) => {
        window.location.replace(result.data.signature_redirect);
      },
      onFail: (error: Error) => {
        // eIdEasy widget will display the error message
        // console.log('error', error);
        // onFail will be called on user error such as inputting wrong id
        // so logging to Sentry as message rather than exception
        if (error.message) {
          Sentry.captureMessage(
            `eIdEasy signing widget error: ${error.message}`,
          );
        }
      },
      methodsConfigFilter: (data: any) => {
        data.signature.forEach((signature: any) => {
          if (signature.actionType.includes(excludedSignatureTypes)) {
            signature.supportedCountries = signature.supportedCountries.filter(
              (country: any) => {
                return !excludedCountries.includes(country);
              },
            );
          }
        });
        return data;
      },
    };

    Object.assign(eidEasyWidget, settings);

    el.appendChild(eidEasyWidget);
  }, [clientId, countryCode, qesUrl, locale, hasAdditionalSigningMethods]);

  return <Box width="100%" data-testid="eid-widget" ref={ref} />;
}
